import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect, Suspense } from 'react';
import { Loader2 } from 'lucide-react';
import { Toaster } from 'sonner';
import { AuthProvider } from './contexts/AuthContext';
import { UploadProgressProvider } from './contexts/UploadProgressContext';
import { ConfirmProvider } from './contexts/ConfirmContext';
import UploadProgressOverlay from './components/UploadProgressOverlay';
import ModaliTrascinabili from './components/ModaliTrascinabili';
import ErrorBoundary from './components/ErrorBoundary';
import ProtectedRoute from './components/ProtectedRoute';
import ForcePasswordChangeGate from './components/ForcePasswordChangeGate';
import Login from './pages/Login';
import HomePage from './pages/HomePage';
// React.lazy con auto-reload one-shot sui chunk failure (post-deploy cache).
import { lazyWithRetry as lazy } from './lib/lazyWithRetry';
import { rootTarget, LAST_PATH_KEY, PUBLIC_PATH_KEY, SKIP_PATHS, ARCHIVIO_SESSION_KEY } from './lib/rootTarget';
import './App.css';

// Pagine pesanti caricate on-demand (riducono il bundle iniziale).
// Login + HomePage restano eager perché sono i due entry point immediati.
const AdminPanel = lazy(() => import('./pages/AdminPanel'));
const ProductsPage = lazy(() => import('./pages/ProductsPage'));
const CatalogsPage = lazy(() => import('./pages/CatalogsPage'));
const CatalogDetailPage = lazy(() => import('./pages/CatalogDetailPage'));
const OrdersPage = lazy(() => import('./pages/OrdersPage'));
const CataloghiFilesPage = lazy(() => import('./pages/CataloghiFilesPage'));
const CataloghiHubPage = lazy(() => import('./pages/CataloghiHubPage'));
const OrdiniCataloghiPage = lazy(() => import('./pages/OrdiniCataloghiPage'));
const PrebookCataloghiPage = lazy(() => import('./pages/PrebookCataloghiPage'));
const PrebookPubblico = lazy(() => import('./pages/PrebookPubblico'));
const PxpOrdinePubblico = lazy(() => import('./pages/PxpOrdinePubblico'));
const PxpSelloutPubblico = lazy(() => import('./pages/PxpSelloutPubblico'));
const ClientCatalog = lazy(() => import('./pages/ClientCatalog'));
const InvitiPublic = lazy(() => import('./pages/InvitiPublic'));
const AffluenzaPublic = lazy(() => import('./pages/AffluenzaPublic'));
const CheckpointPublic = lazy(() => import('./pages/CheckpointPublic'));
const InventarioPublic = lazy(() => import('./pages/InventarioPublic'));
const ShopCatalogPublic = lazy(() => import('./pages/ShopCatalogPublic'));
const ClientiPublic = lazy(() => import('./pages/ClientiPublic'));
const OrdineCataloghiPublic = lazy(() => import('./pages/OrdineCataloghiPublic'));
const VenditeApp = lazy(() => import('./pages/VenditeApp'));
const ContabilitaApp = lazy(() => import('./pages/ContabilitaApp'));
const ProdottiApp = lazy(() => import('./pages/ProdottiApp'));
const InventarioApp = lazy(() => import('./pages/InventarioApp'));
const CreditApp = lazy(() => import('./pages/CreditApp'));
const ClientiApp = lazy(() => import('./pages/ClientiApp'));
const ChatApp = lazy(() => import('./pages/ChatApp'));
const UtilityApp = lazy(() => import('./pages/UtilityApp'));
const ReportApp = lazy(() => import('./pages/ReportApp'));
const BigSpinApp = lazy(() => import('./pages/BigSpinApp'));
const B2BApp = lazy(() => import('./pages/B2BApp'));
const PxpApp = lazy(() => import('./pages/PxpApp'));
const PxpSellout = lazy(() => import('./pages/PxpSellout'));
const PxpStatistiche = lazy(() => import('./pages/PxpStatistiche'));
const PxpProdotti = lazy(() => import('./pages/PxpProdotti'));
const PxpCataloghi = lazy(() => import('./pages/PxpCataloghi'));
const PxpClienti = lazy(() => import('./pages/PxpClienti'));
const PxpVenduto = lazy(() => import('./pages/PxpVenduto'));
const PxpPagamenti = lazy(() => import('./pages/PxpPagamenti'));
const PxpOrdini = lazy(() => import('./pages/PxpOrdini'));
const ComunicazioniApp = lazy(() => import('./pages/ComunicazioniApp'));
const PostaApp = lazy(() => import('./pages/PostaApp'));

// Prefissi delle route pubbliche (link condivisibili). Su queste pagine il
// manifest PWA è gestito da applyAppBranding (lib/publicLink.js).
const PUBLIC_PATH_PREFIXES = ['/catalog/', '/shop-catalog/', '/inviti/', '/affluenza/', '/checkpoint/', '/inventari/', '/clienti-pubblico/', '/ordini-pubblico/', '/prebook-pubblico/', '/pxp-ordine/', '/pxp-sellout/'];
const isPublicPath = (pathname) => PUBLIC_PATH_PREFIXES.some((p) => pathname.startsWith(p));

// Fallback mostrato durante il caricamento di una pagina lazy.
function PageLoader() {
  return (
    <div className="min-h-svh flex items-center justify-center bg-gray-50">
      <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
    </div>
  );
}

// Salva ad ogni navigazione l'ultima route "utile" così quando l'utente
// chiude e riapre lo Shop (o qualsiasi altra app) ci ritorna direttamente.
function PathTracker() {
  const location = useLocation();
  useEffect(() => {
    if (SKIP_PATHS.has(location.pathname)) return;
    if (isPublicPath(location.pathname)) return; // pubbliche (PUBLIC_PATH_PREFIXES), non riportare
    if (location.pathname.startsWith('/archivio')) {
      try { sessionStorage.setItem(ARCHIVIO_SESSION_KEY, '1'); } catch (_) {}
      return;
    }
    try {
      localStorage.setItem(LAST_PATH_KEY, location.pathname + location.search);
    } catch (_) {}
  }, [location]);
  return null;
}

// Sulle route dell'app principale (NON pubbliche) punta il <link rel=manifest>
// a /manifest-app.json, che — a differenza del manifest statico in index.html —
// include start_url + id, requisiti perché Chrome/Android considerino la PWA
// installabile. Il manifest statico resta SENZA start_url apposta: iOS lo legge
// dall'HTML iniziale (ignora le modifiche via JS) e per le pagine pubbliche
// clblnk.it deve defaultare all'URL del documento. Chrome invece rilegge il
// manifest quando cambia l'href → qui lo aggiorniamo a runtime. Le pagine
// pubbliche gestiscono il proprio manifest via applyAppBranding e qui veniamo
// saltati (isPublicPath).
function MainAppManifest() {
  const location = useLocation();
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (isPublicPath(location.pathname)) return;
    const link = document.querySelector('link[rel="manifest"]');
    const manifest = location.pathname.startsWith('/archivio')
      ? '/manifest-archivio.json' : '/manifest-app.json';
    if (link) link.setAttribute('href', manifest);
  }, [location.pathname]);
  return null;
}

// Il consenso delle banche torna sempre su /contabilita (unico redirect
// registrato su Enable Banking): quello dei conti personali dell'Archivio si
// riconosce dallo state "archivio-..." e va li', prima del controllo dei
// permessi della Contabilita'. Vedi services/conti_personali.py.
function RientroBanca({ children }) {
  const p = new URLSearchParams(window.location.search);
  if ((p.get('state') || '').startsWith('archivio-')) {
    return <Navigate to={`/archivio${window.location.search}`} replace />;
  }
  return children;
}

// Entry point '/': la scelta della destinazione sta in lib/rootTarget.js
// (funzione pura, testata); qui restano solo la lettura di localStorage e il
// rendering.
function RootRedirect() {
  let publicPath = null;
  let last = null;
  try {
    publicPath = localStorage.getItem(PUBLIC_PATH_KEY);
    last = localStorage.getItem(LAST_PATH_KEY);
  } catch (_) {}
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  const isPublicHost = host.endsWith('clblnk.it');
  const target = rootTarget({ publicPath, last, isPublicHost });
  if (target) {
    return <Navigate to={target} replace />;
  }
  // Nessuna destinazione utile su dominio pubblico: pagina neutra di "link non
  // valido" invece di rimbalzare al login dell'app interna.
  return (
    <div className="min-h-svh flex items-center justify-center bg-gradient-to-br from-teal-50 to-gray-100 p-6">
      <div className="bg-white rounded-2xl shadow-lg p-8 text-center max-w-md">
        <h1 className="text-xl font-bold text-gray-900 mb-2">Link non valido</h1>
        <p className="text-sm text-gray-600">
          Apri il link condiviso che hai ricevuto.
        </p>
      </div>
    </div>
  );
}

// BigSpin (bigspin.it): la webapp dei clienti delle svendite. Stesso build,
// ma niente del gestionale intorno (login, permessi, banner, manifest app).
// Sugli altri domini si apre sotto /bs, per provarla prima di pubblicarla.
const BigSpinWeb = lazy(() => import('./bigspinweb/BigSpinWeb'));
const BIGSPIN_BASE = (() => {
  if (typeof window === 'undefined') return null;
  const host = window.location.hostname;
  if (host === 'bigspin.it' || host.endsWith('.bigspin.it')) return '';
  const path = window.location.pathname;
  return path === '/bs' || path.startsWith('/bs/') ? '/bs' : null;
})();

function App() {
  if (BIGSPIN_BASE !== null) {
    return (
      <Suspense fallback={<PageLoader />}>
        <BigSpinWeb basename={BIGSPIN_BASE} />
      </Suspense>
    );
  }
  return (
    <UploadProgressProvider>
    <ConfirmProvider>
    <AuthProvider>
      <BrowserRouter>
        <PathTracker />
        <MainAppManifest />
        <ForcePasswordChangeGate />
        <ErrorBoundary>
        <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/catalog/:linkId" element={<ClientCatalog />} />
          <Route path="/inviti/:token" element={<InvitiPublic />} />
          <Route path="/affluenza/:token" element={<AffluenzaPublic />} />
          <Route path="/checkpoint/:token" element={<CheckpointPublic />} />
          <Route path="/inventari/:token" element={<InventarioPublic />} />
          <Route path="/shop-catalog/:token" element={<ShopCatalogPublic />} />
          <Route path="/clienti-pubblico/:token" element={<ClientiPublic />} />
          <Route path="/ordini-pubblico/:token" element={<OrdineCataloghiPublic />} />
          <Route path="/prebook-pubblico/:token" element={<PrebookPubblico />} />
          <Route path="/pxp-ordine/:token" element={<PxpOrdinePubblico />} />
          <Route path="/pxp-sellout/:token" element={<PxpSelloutPubblico />} />

          {/* Home - C-LAB Dashboard */}
          <Route
            path="/home"
            element={
              <ProtectedRoute>
                <HomePage />
              </ProtectedRoute>
            }
          />

          {/* Admin Panel - solo master */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute requireMaster>
                <AdminPanel />
              </ProtectedRoute>
            }
          />

          {/* Cataloghi Hub - aree (prebook, magazzino, ondemand) + Ordini */}
          <Route
            path="/cataloghi"
            element={
              <ProtectedRoute requiredPermission="cataloghi">
                <CataloghiHubPage />
              </ProtectedRoute>
            }
          />
          {/* Area "Ordini": import Excel di ordini già fatti + statistiche.
              Deve precedere le route /cataloghi/:tipo/* (path a 2 segmenti). */}
          <Route
            path="/cataloghi/ordini"
            element={
              <ProtectedRoute requiredPermission="cataloghi">
                <OrdiniCataloghiPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/cataloghi/:tipo/prodotti"
            element={
              <ProtectedRoute requiredPermission="cataloghi">
                <ProductsPage />
              </ProtectedRoute>
            }
          />
          {/* Prebook: area "uguale a Ordini" ma con i dati dall'anagrafica.
              Route statica → precede /cataloghi/:tipo/lista. */}
          <Route
            path="/cataloghi/prebook/lista"
            element={
              <ProtectedRoute requiredPermission="cataloghi">
                <PrebookCataloghiPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/cataloghi/:tipo/lista"
            element={
              <ProtectedRoute requiredPermission="cataloghi">
                <CatalogsPage />
              </ProtectedRoute>
            }
          />
          {/* Editor catalogo (Magazzino/On Demand): ordinamento + sezioni + export. */}
          <Route
            path="/cataloghi/:tipo/catalogo/:id"
            element={
              <ProtectedRoute requiredPermission="cataloghi">
                <CatalogDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/cataloghi/:tipo/ordini"
            element={
              <ProtectedRoute requiredPermission="cataloghi">
                <OrdersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/cataloghi/:tipo/file"
            element={
              <ProtectedRoute requiredPermission="cataloghi">
                <CataloghiFilesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/cataloghi/:tipo/immagini"
            element={
              <ProtectedRoute requiredPermission="cataloghi">
                <CataloghiFilesPage />
              </ProtectedRoute>
            }
          />

          {/* Legacy routes /cataloghi/<sub> senza tipo → ondemand (comportamento attuale) */}
          <Route path="/cataloghi/prodotti" element={<Navigate to="/cataloghi/ondemand/prodotti" replace />} />
          <Route path="/cataloghi/lista" element={<Navigate to="/cataloghi/ondemand/lista" replace />} />
          {/* /cataloghi/ordini è ora l'area "Ordini" (vedi route sopra); gli ordini
              ricevuti restano su /cataloghi/<tipo>/ordini. */}
          <Route path="/cataloghi/file" element={<Navigate to="/cataloghi/ondemand/file" replace />} />
          <Route path="/cataloghi/immagini" element={<Navigate to="/cataloghi/ondemand/immagini" replace />} />

          {/* BigSpin — pannello sito B2C (www.bigspin.it) */}
          <Route
            path="/bigspin"
            element={
              <ProtectedRoute requiredPermission="bigspin">
                <BigSpinApp />
              </ProtectedRoute>
            }
          />

          {/* BigSpin B2B — pannello sito rivenditori (b2b.clabnet.it) */}
          <Route
            path="/b2b"
            element={
              <ProtectedRoute requiredPermission="b2b">
                <B2BApp />
              </ProtectedRoute>
            }
          />

          {/* Vendite App Route */}
          <Route
            path="/vendite"
            element={
              <ProtectedRoute requiredPermission="vendite">
                <VenditeApp />
              </ProtectedRoute>
            }
          />

          {/* Contabilità App Route (ex Ingrosso) */}
          <Route
            path="/contabilita"
            element={
              <RientroBanca>
                <ProtectedRoute requiredPermission="contabilita">
                  <ContabilitaApp />
                </ProtectedRoute>
              </RientroBanca>
            }
          />
          {/* Redirect legacy /ingrosso → /contabilita */}
          <Route path="/ingrosso" element={<Navigate to="/contabilita" replace />} />

          {/* Prodotti (Master Data) App Route */}
          <Route
            path="/prodotti"
            element={
              <ProtectedRoute requiredPermission="prodotti">
                <ProdottiApp />
              </ProtectedRoute>
            }
          />

          {/* Inventario App Route (estratta da Prodotti) */}
          <Route
            path="/inventario"
            element={
              <ProtectedRoute requiredPermission="inventario">
                <InventarioApp />
              </ProtectedRoute>
            }
          />

          {/* Credit Management App Route */}
          <Route
            path="/credit"
            element={
              <ProtectedRoute requiredPermission="credit">
                <CreditApp />
              </ProtectedRoute>
            }
          />

          {/* Clienti App Route */}
          <Route
            path="/clienti"
            element={
              <ProtectedRoute requiredPermission="clienti">
                <ClientiApp />
              </ProtectedRoute>
            }
          />

          {/* Chat — Assistente AI */}
          <Route
            path="/chat"
            element={
              <ProtectedRoute requiredPermission="chat">
                <ChatApp />
              </ProtectedRoute>
            }
          />

          {/* Utility — calcolatrici + convertitori file */}
          <Route
            path="/utility"
            element={
              <ProtectedRoute requiredPermission="utility">
                <UtilityApp />
              </ProtectedRoute>
            }
          />

          {/* PXP — hub dell'azienda PXP. Le sotto-app stanno sotto /pxp/*
              e condividono il permesso `pxp`. */}
          <Route
            path="/pxp"
            element={
              <ProtectedRoute requiredPermission="pxp">
                <PxpApp />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pxp/sellout"
            element={
              <ProtectedRoute requiredPermission="pxp">
                <PxpSellout />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pxp/ordini"
            element={
              <ProtectedRoute requiredPermission="pxp">
                <PxpOrdini />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pxp/venduto"
            element={
              <ProtectedRoute requiredPermission="pxp">
                <PxpVenduto />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pxp/clienti"
            element={
              <ProtectedRoute requiredPermission="pxp">
                <PxpClienti />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pxp/statistiche"
            element={
              <ProtectedRoute requiredPermission="pxp">
                <PxpStatistiche />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pxp/prodotti"
            element={
              <ProtectedRoute requiredPermission="pxp">
                <PxpProdotti />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pxp/cataloghi"
            element={
              <ProtectedRoute requiredPermission="pxp">
                <PxpCataloghi />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pxp/pagamenti"
            element={
              <ProtectedRoute requiredPermission="pxp">
                <PxpPagamenti />
              </ProtectedRoute>
            }
          />

          {/* Archivio (ex Comunicazioni) — mail, WhatsApp e file in un posto
              solo. Contiene posta personale dell'owner: permesso dedicato, non
              nei default. App a se': niente tessera in home, manifest proprio
              (/manifest-archivio.json) per installarla da sola. */}
          <Route
            path="/archivio"
            element={
              <ProtectedRoute requiredPermission="comunicazioni">
                <ComunicazioniApp />
              </ProtectedRoute>
            }
          />
          <Route path="/comunicazioni" element={<Navigate to="/archivio" replace />} />

          {/* Posta — client di posta sulle caselle dell'Archivio (stesso permesso). */}
          <Route
            path="/posta"
            element={
              <ProtectedRoute requiredPermission="comunicazioni">
                <PostaApp />
              </ProtectedRoute>
            }
          />

          {/* Report — report ed export di dati */}
          <Route
            path="/report"
            element={
              <ProtectedRoute requiredPermission="report">
                <ReportApp />
              </ProtectedRoute>
            }
          />

          {/* Legacy routes redirect */}
          <Route path="/dashboard" element={<Navigate to="/cataloghi" replace />} />
          <Route path="/products" element={<Navigate to="/cataloghi/ondemand/prodotti" replace />} />
          <Route path="/catalogs" element={<Navigate to="/cataloghi/ondemand/lista" replace />} />
          <Route path="/orders" element={<Navigate to="/cataloghi/ondemand/ordini" replace />} />
          <Route path="/uploads" element={<Navigate to="/cataloghi/ondemand/file" replace />} />

          {/* Default redirect: torna all'ultima pagina visitata, altrimenti /login */}
          <Route path="/" element={<RootRedirect />} />
        </Routes>
        </Suspense>
        </ErrorBoundary>
      </BrowserRouter>
      <UploadProgressOverlay />
      <ModaliTrascinabili />
      <Toaster position="top-center" richColors />
    </AuthProvider>
    </ConfirmProvider>
    </UploadProgressProvider>
  );
}

export default App;
